<template>
  <v-card flat class="serialized-report">
    <v-toolbar dense flat color="grey lighten-3" class="no-print">
      <v-btn icon :aria-label="backLabel" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title>{{ heading }}</v-toolbar-title>
      <v-spacer />
      <v-chip small color="primary" text-color="white">
        {{ visibleSerialCount }} serial{{ visibleSerialCount === 1 ? "" : "s" }}
      </v-chip>
    </v-toolbar>

    <v-card-text class="serialized-report__body">
      <div class="text-subtitle-2 admin-muted mb-3">{{ breadcrumb }}</div>
      <v-text-field
        v-model.trim="search"
        outlined
        dense
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
        :label="searchLabel"
        class="serialized-report__search mb-4"
      />

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-alert v-else-if="error" type="error" outlined>
        {{ error }}
        <v-btn text color="primary" class="ml-2" @click="loadReport">Try again</v-btn>
      </v-alert>

      <v-alert v-else-if="!currentItems.length" type="info" outlined>
        {{ emptyMessage }}
      </v-alert>

      <v-list v-else two-line class="pa-0 serialized-report__list">
        <template v-for="(item, index) in currentItems">
          <v-list-item :key="item.key" button @click="openItem(item)">
            <v-list-item-avatar color="primary">
              <v-icon dark>{{ item.icon }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title
                class="font-weight-bold"
                v-html="$options.filters.highlight(item.name, search)"
              />
              <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="serialized-report__row-action">
              <strong>{{ currency(item.totalCost) }}</strong>
              <v-icon v-if="level !== 'serials'">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="index < currentItems.length - 1" :key="`${item.key}-divider`" />
        </template>
      </v-list>
    </v-card-text>
    <v-divider />
    <v-card-actions class="serialized-report__total pa-4">
      <div>
        <div class="text-caption admin-muted">Total inventory cost</div>
        <div class="text-body-2">{{ visibleSerialCount }} serial{{ visibleSerialCount === 1 ? "" : "s" }}</div>
      </div>
      <v-spacer />
      <strong class="text-h6">{{ currency(visibleCostTotal) }}</strong>
    </v-card-actions>
  </v-card>
</template>

<script>
import reportService from "@/modules/ReportView/service";

export default {
  name: "serialized-inventory-report",
  data: () => ({
    loading: false,
    error: "",
    search: "",
    serials: [],
    level: "categories",
    selectedCategory: null,
    selectedProduct: null,
  }),
  computed: {
    locationId() {
      return Number(this.$route.query.LocationId);
    },
    heading() {
      if (this.level === "products") return this.selectedCategory.name;
      if (this.level === "serials") return this.selectedProduct.name;
      return "Serialized Inventory";
    },
    breadcrumb() {
      const parts = ["Serialized Inventory"];
      if (this.selectedCategory) parts.push(this.selectedCategory.name);
      if (this.selectedProduct) parts.push(this.selectedProduct.name);
      return parts.join("  /  ");
    },
    backLabel() {
      return this.level === "categories" ? "Back to location" : "Back one level";
    },
    searchLabel() {
      if (this.level === "products") return "Search products";
      if (this.level === "serials") return "Search serial numbers";
      return "Search categories";
    },
    emptyMessage() {
      if (this.search) return "No matching inventory was found.";
      return "No available serialized inventory was found for this location.";
    },
    categories() {
      const grouped = new Map();
      this.serials.forEach((serial) => {
        const product = serial.Product || {};
        const override = product.LocationCategoryOverride;
        const category = override && override.Category
          ? override.Category
          : product.Category;
        const key = category && category.id ? `category-${category.id}` : "uncategorized";
        if (!grouped.has(key)) {
          grouped.set(key, {
            key,
            id: category && category.id,
            name: category && category.name ? category.name : "Uncategorized",
            serials: [],
          });
        }
        grouped.get(key).serials.push(serial);
      });
      return Array.from(grouped.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    products() {
      if (!this.selectedCategory) return [];
      const grouped = new Map();
      this.selectedCategory.serials.forEach((serial) => {
        const product = serial.Product || {};
        const key = product.id ? `product-${product.id}` : `product-${product.name || "unknown"}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            key,
            id: product.id,
            name: product.name || "Unknown product",
            sku: product.sku || "SKU not recorded",
            serials: [],
          });
        }
        grouped.get(key).serials.push(serial);
      });
      return Array.from(grouped.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    currentItems() {
      const query = (this.search || "").toLowerCase();
      if (this.level === "serials") {
        return (this.selectedProduct ? this.selectedProduct.serials : [])
          .map((serial) => ({
            key: `serial-${serial.id}`,
            name: serial.serialNumber || "Serial number not recorded",
            subtitle: [serial.condition || "New", this.currency(serial.cost)].join(" • "),
            icon: "mdi-barcode",
            totalCost: Number(serial.cost) || 0,
          }))
          .filter((item) => item.name.toLowerCase().includes(query));
      }
      if (this.level === "products") {
        return this.products
          .map((product) => ({
            ...product,
            subtitle: `${product.serials.length} available • ${product.sku}`,
            icon: "mdi-package-variant-closed",
            totalCost: this.serialCostTotal(product.serials),
          }))
          .filter((item) => `${item.name} ${item.sku}`.toLowerCase().includes(query));
      }
      return this.categories
        .map((category) => ({
          ...category,
          subtitle: `${category.serials.length} available serial${category.serials.length === 1 ? "" : "s"}`,
          icon: "mdi-shape-outline",
          totalCost: this.serialCostTotal(category.serials),
        }))
        .filter((item) => item.name.toLowerCase().includes(query));
    },
    visibleSerialCount() {
      if (this.level === "serials") return this.currentItems.length;
      return this.currentItems.reduce((total, item) => total + item.serials.length, 0);
    },
    visibleCostTotal() {
      return this.currentItems.reduce((total, item) => total + (Number(item.totalCost) || 0), 0);
    },
  },
  created() {
    this.loadReport();
  },
  methods: {
    async loadReport() {
      if (!Number.isSafeInteger(this.locationId) || this.locationId <= 0) {
        this.error = "A valid location is required to open this report.";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const response = await reportService.Location.serializedInventory({
          LocationId: this.locationId,
        });
        this.serials = response.data || [];
      } catch (error) {
        this.error = "Serialized inventory could not be loaded. Check your connection and try again.";
      } finally {
        this.loading = false;
      }
    },
    openItem(item) {
      if (this.level === "categories") {
        this.selectedCategory = item;
        this.level = "products";
        this.search = "";
      } else if (this.level === "products") {
        this.selectedProduct = item;
        this.level = "serials";
        this.search = "";
      }
    },
    goBack() {
      if (this.level === "serials") {
        this.selectedProduct = null;
        this.level = "products";
        this.search = "";
        return;
      }
      if (this.level === "products") {
        this.selectedCategory = null;
        this.level = "categories";
        this.search = "";
        return;
      }
      this.$router.back();
    },
    currency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number(value || 0));
    },
    serialCostTotal(serials) {
      return (serials || []).reduce((total, serial) => total + (Number(serial.cost) || 0), 0);
    },
  },
};
</script>

<style scoped>
.serialized-report {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  height: calc(100dvh - 48px);
  min-height: 420px;
  overflow: hidden;
}

.serialized-report__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
}

.serialized-report__search {
  max-width: 520px;
}

.serialized-report__list {
  border: 1px solid var(--v-primary-base);
  border-radius: 12px;
  overflow: hidden;
}

.serialized-report__row-action {
  align-items: flex-end;
  flex-direction: row;
  gap: 8px;
  margin-left: 12px;
}

.serialized-report__row-action strong {
  color: var(--v-primary-base);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.serialized-report__total {
  background: var(--brand-bg);
  flex: 0 0 auto;
  z-index: 2;
}

@media (max-width: 420px) {
  .serialized-report__row-action { margin-left: 6px; }
  .serialized-report__row-action strong { font-size: .875rem; }
}
</style>
